/**
 * ST-05 (AIVA-6805) — the one "All actions" list behind Ctrl+Shift+K in
 * PowerPoint: the native AiVA actions first, then Deckorate's design catalog
 * when the persona has Deckorate.
 *
 * The AiVA items are mapped onto Deckorate's command shape (kind "aiva") so
 * both lists share Deckorate's palette, ranking, recents and favorites. The
 * Office Dialog is a separate page with no access to the pane's skills cache,
 * so the pane hands it the sources in its URL and the dialog rebuilds the
 * same list (encodeCatalogSources / readCatalogSources).
 */

import type { DeckorateCommand } from "../../../../deckorate/types";
import { ACTION_CATALOG } from "../../../../deckorate/features/command-palette/services/catalog";
import type { PaletteGroup, PaletteItem } from "../types";
import { buildPaletteItems, type PaletteSkill } from "./paletteItems";

export interface CatalogSources {
  readonly skills: readonly PaletteSkill[];
  /** Deckorate is in the persona's module list: its design actions join the list. */
  readonly deckorate: boolean;
}

const GROUP_STYLE: Readonly<Record<PaletteGroup, { readonly category: string; readonly icon: string }>> = {
  Deck: { category: "PowerPoint", icon: "📑" },
  Skills: { category: "Skill", icon: "⚡" },
  AiVA: { category: "AiVA", icon: "💬" },
};

export function toAivaCommand(item: PaletteItem): DeckorateCommand {
  const style = GROUP_STYLE[item.group];
  return {
    id: item.id,
    label: item.label,
    category: style.category,
    icon: style.icon,
    description: item.description,
    // The list shows label and category only, so the description is searched too.
    keywords: [...item.keywords, item.description],
    kind: "aiva",
    aivaAction: item.action,
  };
}

export function buildAllActionsCatalog(sources: CatalogSources): DeckorateCommand[] {
  const aiva = buildPaletteItems(sources).map(toAivaCommand);
  return sources.deckorate ? [...aiva, ...ACTION_CATALOG] : aiva;
}

/** Query parameter the dialog page reads its sources from. */
export const CATALOG_PARAM = "actions";

/**
 * Compact on purpose: the dialog URL has a length limit. Skill descriptions
 * are dropped (the dialog list never shows them), which only costs the dialog
 * matching on a description word.
 */
export function encodeCatalogSources(sources: CatalogSources): string {
  const compact = {
    d: sources.deckorate ? 1 : 0,
    s: sources.skills.map((s) => [s.slash, s.label]),
  };
  return `${CATALOG_PARAM}=${encodeURIComponent(JSON.stringify(compact))}`;
}

function isPairList(value: unknown): value is [string, string][] {
  return (
    Array.isArray(value) &&
    value.every((p) => Array.isArray(p) && p.length === 2 && typeof p[0] === "string" && typeof p[1] === "string")
  );
}

/**
 * The sources a pane passed in `search`, or null when there are none (or they
 * don't parse) — the dialog then shows Deckorate's own catalog, as before ST-05.
 */
export function readCatalogSources(search: string): CatalogSources | null {
  const raw = new URLSearchParams(search).get(CATALOG_PARAM);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { d?: unknown; s?: unknown };
    if (!isPairList(parsed.s)) return null;
    return {
      deckorate: parsed.d === 1,
      skills: parsed.s.map(([slash, label]) => ({ slash, label, description: "" })),
    };
  } catch {
    return null;
  }
}
